import {announce} from '@github-ui/aria-live'
import {useMutation} from '@github-ui/react-query'
import {reactFetchJSON} from '@github-ui/verified-fetch'
import {
  createContext,
  type Dispatch,
  type PropsWithChildren,
  type SetStateAction,
  use,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import {type PersonaOnboardingPayload, type PersonaOnboardingUpdate, personas} from '../data/personas'
import {reportPersonaTasksDismissed} from '../utils/persona-onboarding-telemetry'

const PersonaOnboardingContext = createContext<PersonaOnboardingPayload | undefined>(undefined)
const SetPersonaOnboardingContext = createContext<
  Dispatch<SetStateAction<PersonaOnboardingPayload | undefined>> | undefined
>(undefined)
const DismissSuggestedTasksContext = createContext<(() => boolean) | undefined>(undefined)
const SuggestedTasksDismissalErrorContext = createContext<Error | null>(null)
const PersonaTaskIntroContext = createContext(false)
const SetPersonaTaskIntroContext = createContext<Dispatch<SetStateAction<boolean>> | undefined>(undefined)
const dismissalErrorMessage =
  'We could not confirm your dismissal. Suggested tasks have been restored. Please try Dismiss suggested tasks again.'

export function PersonaOnboardingProvider({
  initialPayload,
  children,
}: PropsWithChildren<{initialPayload?: PersonaOnboardingPayload}>) {
  const [onboarding, setOnboarding] = useState(initialPayload)
  const [optimisticDismissal, setOptimisticDismissal] = useState(false)
  const [introAvailable, setIntroAvailable] = useState(false)
  const dismissalPendingRef = useRef(false)
  const {mutate, error} = useMutation({
    mutationFn: (previous: PersonaOnboardingPayload | undefined) =>
      savePersonaOnboarding(previous, {status: 'suggested_tasks_dismissed'}),
    onSuccess: (saved, previous) => {
      setOnboarding(current => {
        if (current === previous) return saved
        return current ? {...current, suggestedTasksDismissed: true} : current
      })
    },
    onSettled: () => {
      dismissalPendingRef.current = false
      setOptimisticDismissal(false)
    },
  })
  useEffect(() => {
    if (error) announce(dismissalErrorMessage)
  }, [error])
  const dismissSuggestedTasks = useCallback(() => {
    if (dismissalPendingRef.current || onboarding?.suggestedTasksDismissed) return false
    dismissalPendingRef.current = true
    setOptimisticDismissal(true)
    if (onboarding?.persona) reportPersonaTasksDismissed(onboarding.persona)
    mutate(onboarding)
    announce('Suggested tasks dismissed.')
    return true
  }, [mutate, onboarding])
  // Keep optimism separate so failure cannot overwrite newer authoritative context.
  const visibleOnboarding = useMemo(
    () => (optimisticDismissal && onboarding ? {...onboarding, suggestedTasksDismissed: true} : onboarding),
    [onboarding, optimisticDismissal],
  )

  return (
    <PersonaOnboardingContext value={visibleOnboarding}>
      <SetPersonaOnboardingContext value={setOnboarding}>
        <DismissSuggestedTasksContext value={dismissSuggestedTasks}>
          <SuggestedTasksDismissalErrorContext value={error}>
            <PersonaTaskIntroContext value={introAvailable}>
              <SetPersonaTaskIntroContext value={setIntroAvailable}>{children}</SetPersonaTaskIntroContext>
            </PersonaTaskIntroContext>
          </SuggestedTasksDismissalErrorContext>
        </DismissSuggestedTasksContext>
      </SetPersonaOnboardingContext>
    </PersonaOnboardingContext>
  )
}

export function usePersonaOnboarding() {
  return use(PersonaOnboardingContext)
}

export function usePersonaTaskIntro() {
  const introAvailable = use(PersonaTaskIntroContext)
  const setIntroAvailable = use(SetPersonaTaskIntroContext)
  // This mount keeps its intro while the provider prevents replay after navigation or rollback.
  const [animate] = useState(introAvailable)
  useEffect(() => {
    setIntroAvailable?.(false)
  }, [setIntroAvailable])
  return animate
}

export function useSetPersonaOnboarding() {
  return use(SetPersonaOnboardingContext)
}

export function useSuggestedTasksDismissal() {
  const dismiss = use(DismissSuggestedTasksContext)
  const error = use(SuggestedTasksDismissalErrorContext)
  if (!dismiss) throw new Error('Suggested tasks require PersonaOnboardingProvider.')
  return {dismiss, errorMessage: error ? dismissalErrorMessage : undefined}
}

function isPersonaOnboardingPayload(value: unknown): value is PersonaOnboardingPayload {
  return (
    typeof value === 'object' &&
    value !== null &&
    'enabled' in value &&
    typeof value.enabled === 'boolean' &&
    'show' in value &&
    typeof value.show === 'boolean' &&
    'persona' in value &&
    (value.persona === null || personas.some(persona => persona.id === value.persona)) &&
    'savePath' in value &&
    typeof value.savePath === 'string' &&
    value.savePath.length > 0 &&
    (!('suggestedTasksDismissed' in value) || typeof value.suggestedTasksDismissed === 'boolean')
  )
}

async function savePersonaOnboarding(
  onboarding: PersonaOnboardingPayload | undefined,
  update: PersonaOnboardingUpdate,
) {
  if (!onboarding?.enabled) throw new Error('Guided setup is unavailable. Please reload the page.')
  try {
    const response = await reactFetchJSON(onboarding.savePath, {
      method: 'PATCH',
      body: update,
      signal: AbortSignal.timeout(15_000),
      ...(update.status === 'suggested_tasks_dismissed' ? {keepalive: true} : {}),
    })
    if (!response.ok) throw new Error('Saving guided setup failed.')
    const saved: unknown = await response.json()
    if (!isPersonaOnboardingPayload(saved)) throw new Error('Guided setup returned an invalid response.')
    const confirmed =
      update.status === 'suggested_tasks_dismissed'
        ? saved.suggestedTasksDismissed === true &&
          saved.show === onboarding.show &&
          saved.persona === onboarding.persona
        : !saved.show && saved.persona === (update.status === 'completed' ? update.persona : null)
    if (!confirmed) throw new Error('The response did not confirm the guided setup update.')
    return saved
  } catch {
    throw new Error('We could not save your guided setup. Please try again.')
  }
}

export function useSavePersonaOnboarding(
  onSuccess?: (saved: PersonaOnboardingPayload, update: PersonaOnboardingUpdate) => void,
) {
  const onboarding = usePersonaOnboarding()
  const setOnboarding = useSetPersonaOnboarding()
  const setIntroAvailable = use(SetPersonaTaskIntroContext)

  return useMutation({
    mutationFn: (update: PersonaOnboardingUpdate) => {
      if (!setOnboarding) throw new Error('Guided setup is unavailable. Please reload the page.')
      return savePersonaOnboarding(onboarding, update)
    },
    onSuccess: (saved, update) => {
      if (update.status === 'completed' && !saved.show && saved.persona === update.persona) {
        setIntroAvailable?.(true)
      }
      setOnboarding?.(current =>
        current?.suggestedTasksDismissed === true ? {...saved, suggestedTasksDismissed: true} : saved,
      )
      onSuccess?.(saved, update)
    },
  })
}
